/* TTL — bulletproof autoplay-loop video manager
 * Targets the TTL-authored looping videos only (feature videos, "listened"
 * section, product-gallery video). The theme's own video-component manages its
 * media-grid / promo videos itself; managing those here too made the two fight.
 * Fixes:
 *  - Videos pausing when tab loses focus (visibilitychange resume)
 *  - iOS bfcache wiping playback state (pageshow resume)
 *  - Browser audio-focus / video-pool eviction pausing background videos (pause-event recovery)
 *  - Battery drain from offscreen videos (IntersectionObserver pause/play)
 * Runs once globally; idempotent via WeakSet so multiple imports are safe.
 */
(function () {
  if (window.__ttlVideoLoopsBound) return;
  window.__ttlVideoLoopsBound = true;

  const SELECTOR = '.ttl-fv__video, .ttl-listened__video, .ttl-gallery-video__el';
  const bound = new WeakSet();

  function safePlay(v) {
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(function () { /* autoplay policy */ });
  }

  function bind(v) {
    if (bound.has(v)) return;
    bound.add(v);

    // Initial kick (in case autoplay attribute alone failed)
    if (v.readyState >= 2) safePlay(v);
    else v.addEventListener('loadeddata', function () { safePlay(v); }, { once: true });

    // Re-kick on unexpected pause (e.g. browser bumped this video out of the active pool)
    v.addEventListener('pause', function () {
      if (document.hidden) return;
      if (v.dataset.ttlIntentionalPause === '1') return;
      setTimeout(function () { safePlay(v); }, 40);
    });

    // Pause when offscreen, resume when scrolled back into view
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            v.dataset.ttlIntentionalPause = '0';
            safePlay(v);
          } else {
            v.dataset.ttlIntentionalPause = '1';
            v.pause();
          }
        });
      }, { threshold: 0.15 });
      io.observe(v);
    }
  }

  function scan() {
    document.querySelectorAll(SELECTOR).forEach(bind);
  }

  // Initial scan
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  // Re-scan when sections load dynamically (Shopify theme editor, app blocks)
  document.addEventListener('shopify:section:load', scan);

  // Watch the DOM for any video element added later
  if ('MutationObserver' in window) {
    new MutationObserver(function (muts) {
      for (const m of muts) {
        for (const node of m.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(SELECTOR)) bind(node);
          if (node.querySelectorAll) node.querySelectorAll(SELECTOR).forEach(bind);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  // Resume everything when tab returns
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) return;
    document.querySelectorAll(SELECTOR).forEach(function (v) {
      if (v.dataset.ttlIntentionalPause !== '1') safePlay(v);
    });
  });

  // iOS bfcache (back/forward navigation)
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    document.querySelectorAll(SELECTOR).forEach(function (v) {
      if (v.dataset.ttlIntentionalPause !== '1') safePlay(v);
    });
  });
})();
